'use client';
/* eslint-disable jsx-a11y/no-redundant-roles --
   The table is laid out with `display: grid` (CMS-1772 R2), and Chrome and
   WebKit drop the native table semantics whenever `display` is overridden. The
   roles below look redundant against the markup but are what keeps the table in
   the accessibility tree, so they must stay. */
/* eslint-disable tailwindcss/no-custom-classname */
/* eslint-disable tailwindcss/no-arbitrary-value */
/* eslint-disable tailwindcss/no-unnecessary-arbitrary-value */
import classNames from 'classnames';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  CSSProperties,
} from 'react';
import { hasOrAwaitsValue } from '../../calculatedAttributes/resolve';
import { useCalculatedAttributeResolver } from '../../calculatedAttributes/useCalculatedAttributeResolver';
import ChevronRight from '../../components/icons/ChevronRight';
import { QuoteBasisDisclaimer } from '../../components/QuoteBasisDisclaimer';
import { ATTR_NAMES } from '../../types/attrNames';
import { TravelTripCriteria } from '../../types/travelInsurance';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { TableRuleButton } from '../ComparisonTable/components/tableRuleButton';
import { CategoryRow } from './components/CategoryRow';
import { IdentityCell } from './components/IdentityCell';
import { LoanBasisNote } from './components/LoanBasisNote';
import { ValueCell } from './components/ValueCell';
import {
  CELL_PADDING,
  HOT_REWARD_HEADER_TEXT,
  IDENTITY_CELL_PADDING,
  NEUTRAL_HEADER_TEXT,
} from './constants';
import { TRANSLATIONS } from './i18n';
import { ComponentTableBuilder } from './types';
import {
  buildColumnTemplates,
  isRenderedColumn,
  isTiPriceColumn,
  resolveCell,
} from './utils';

const MAX_VISIBLE_ROWS = 8;

export const TableBuilder = (props: ComponentTableBuilder) => {
  const {
    anchorId,
    title,
    subtitle,
    heightVariant,
    mode = 'GENERAL',
    visibilityDesktop,
    visibilityMobile,
    columns,
    rows,
    pushDataLayer,
    defaultTravelAsFamily,
    defaultDestination,
    defaultInsuranceType,
    endDateOffsetDays,
    vertical,
    pageType,
    loanSearchParams,
    defaultRegion,
    loanAmount,
    loanTenure,
  } = props;

  const trimmedAnchorId = anchorId?.trim() || undefined;

  // The table's authored trip (CMS-1851): one object for the CTA fallback in
  // every identity cell.
  const defaultTripCriteria: TravelTripCriteria = {
    insuranceType: defaultInsuranceType,
    travelAsFamily: defaultTravelAsFamily,
    destination: defaultDestination,
    region: defaultRegion,
    endDateOffsetDays,
  };

  const context = useCmsClientContext();
  const staticText = getClientTranslations(TRANSLATIONS, context);

  // The engine facade: assembles CalculationSources from context, so this
  // block never builds them by hand and cannot forget a provider's slice.
  const resolveCalculated = useCalculatedAttributeResolver();

  const [showAll, setShowAll] = useState(false);
  const [[showLeft, showRight], setShow] = useState([false, false]);
  const tableRef = useRef<HTMLDivElement | null>(null);

  // Memoized so the reference is stable for the memos hanging off it
  // (`hasResolvedQuotes`) — a fresh array every render would defeat them.
  const standardRows = useMemo(
    () => rows.filter((r) => r.rowType === 'STANDARD'),
    [rows],
  );
  const isShowMoreAvailable = standardRows.length > MAX_VISIBLE_ROWS;
  const isShowMoreVisible = !showAll && isShowMoreAvailable;

  // Standard rows only, mirroring the row loop's own cut.
  const visibleStandardRows = useMemo(
    () =>
      isShowMoreVisible
        ? standardRows.slice(0, MAX_VISIBLE_ROWS)
        : standardRows,
    [isShowMoreVisible, standardRows],
  );

  const isTravelInsurance = mode === 'TRAVEL_INSURANCE';
  const isPersonalLoan = mode === 'PERSONAL_LOAN';
  const loanTerms = useMemo(
    () =>
      isPersonalLoan && loanAmount && loanTenure
        ? { amount: loanAmount, tenure: loanTenure }
        : undefined,
    [isPersonalLoan, loanAmount, loanTenure],
  );

  // A TI_PREMIUM_PRICE column retained in Strapi after the mode moved away must
  // not render (CMS-1896 AC-8) — there are no quotes to resolve it against
  // outside TI mode, and an all-"-" column is worse than no column. Each entry
  // keeps its ORIGINAL index: `valueCell` is positional, so re-indexing would
  // shift every later column onto the wrong cell.
  const renderedColumns = useMemo(
    () =>
      columns
        .map((column, index) => ({ column, index }))
        .filter(({ column }) =>
          isRenderedColumn(column, mode, resolveCalculated),
        ),
    [columns, mode, resolveCalculated],
  );

  // The rendered set, not the authored one: a dormant price column must not pull
  // the disclaimer back onto the page.
  const hasPriceColumn = renderedColumns.some(({ column }) =>
    isTiPriceColumn(column, resolveCalculated),
  );

  // Total columns = 1 (identity) + data columns
  const totalColumns = renderedColumns.length + 1;

  // Whether THIS table priced any of its own rows: the disclaimer states the
  // basis for "prices shown", so it must not sit above an all-"-" column.
  const hasResolvedQuotes = useMemo(() => {
    if (!hasPriceColumn) return false;

    // On-screen rows only — the basis describes the prices shown. A row that
    // has a price and one still waiting for it both count: the paragraph sits
    // above the table, so unmounting it mid-poll shifts the table vertically
    // (CLS), and the cells show loaders meanwhile, which is exactly what the
    // disclaimer describes.
    //
    // Deliberate residual: a poll that settles with ZERO matching quotes flips
    // this to false and unmounts the paragraph — one shift, in the edge case
    // where the whole column falls to "-" and the basis sentence would be
    // describing prices that are not there.
    return visibleStandardRows.some((row) =>
      hasOrAwaitsValue(
        resolveCalculated(
          ATTR_NAMES.TI_PREMIUM_PRICE,
          row.identityCellProductRef,
        ),
      ),
    );
  }, [hasPriceColumn, visibleStandardRows, resolveCalculated]);

  // Column widths live on the table as grid tracks (CMS-1772 R2). One custom
  // property per breakpoint, because the track list depends on the authored
  // column set and so cannot be written as a static responsive class.
  const columnTemplates = useMemo(
    () =>
      buildColumnTemplates(
        renderedColumns.map(({ column }) => ({
          ...column,
          isTiPrice: isTiPriceColumn(column, resolveCalculated),
        })),
        mode,
      ),
    [renderedColumns, resolveCalculated, mode],
  );

  const responsiveTableTemplates = useMemo(
    () =>
      ({
        '--tb-columns-sm': columnTemplates.sm,
        '--tb-columns-md': columnTemplates.md,
        '--tb-columns-lg': columnTemplates.lg,
        '--tb-columns-xl': columnTemplates.xl,
      }) as CSSProperties,
    [columnTemplates],
  );

  useEffect(() => {
    const el = tableRef.current;
    if (!el) return;

    const updateOverflow = () => {
      const hasOverflow = el.scrollWidth > el.clientWidth;
      const showLeft = !!el.scrollLeft;
      const offsetThrottle = 10;
      const showRight =
        hasOverflow &&
        el.clientWidth + el.scrollLeft <= el.scrollWidth - offsetThrottle;
      setShow([showLeft, showRight]);
    };

    updateOverflow();

    const ro = new ResizeObserver(updateOverflow);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const tableScrollHandler = useCallback(
    (e: { currentTarget: HTMLDivElement }) => {
      const t = e.currentTarget;
      const showLeft = !!t.scrollLeft;
      const offsetThrottle = 10;
      const showRight =
        t.clientWidth + t.scrollLeft <= t.scrollWidth - offsetThrottle;
      setShow([showLeft, showRight]);
    },
    [],
  );

  // Track visible row count for show more logic
  let visibleRowCount = 0;

  const visibilityClass = classNames({
    'hidden md:block': visibilityDesktop && !visibilityMobile,
    'block md:hidden': !visibilityDesktop && visibilityMobile,
    hidden: !visibilityDesktop && !visibilityMobile,
  });

  return (
    <div className={visibilityClass} id={trimmedAnchorId}>
      {/* The module header, restyled by Kimchi Yim on 2026-08-30 (CMS-1921
          review items 3, 4 and 6): the title is a module heading rather than a
          list-level one, the description is body copy at the relaxed line
          height, and the gap below is the responsive `gap/y` token (24px at
          1280, 16px at 360) instead of a flat 16px.

          This block is shared with General and Travel Insurance tables, so all
          three modes take the new header. */}
      {(title || subtitle) && (
        // One `gap/y` under every line of the header, not a tighter gap inside
        // it: the subtitle is a line of the header, so it is spaced from the
        // title exactly as the header is spaced from the table (CMS-1921 QA
        // round 2). With no subtitle the wrapper's own margin carries that same
        // gap from the title to whatever follows.
        <div className="mb-spacing/gap/y flex flex-col gap-spacing/gap/y">
          {title && (
            <h3 className="mhg-alpha-typography-display-1 text-text/primary">
              {title}
            </h3>
          )}
          {subtitle && (
            // The token is reached as an arbitrary value, the way
            // `--text-secondary-lighter` already is elsewhere in this package.
            // Neither plain name gives it: `text-text/secondary` is the
            // Tailwind theme key the legacy palette shadows with #42424A, and
            // `text-secondary` is the brand's secondary COLOUR (a blue), not
            // the secondary TEXT colour.
            <p className="mhg-alpha-typography-body-2-relaxed text-[--text-secondary]">
              {subtitle}
            </p>
          )}
        </div>
      )}
      {/* Only where a date assumption actually exists — that is, on a Travel
          Insurance table that has a Price column, and never on other modes
          (CMS-1896 R6, as amended by Kimchi Yim on 2026-08-10).

          R6's original text asked for this on every TI table, reasoning that
          attribute values are "calculated from the TI parameter fields". They
          are not: attribute columns render static values authored in Strapi and
          the Table Builder makes no quote call of its own, so a TI table with
          no Price column has no quote basis to disclaim. The ticket body still
          carries the superseded wording. */}
      {isPersonalLoan && (
        <LoanBasisNote loanAmount={loanAmount} loanTenure={loanTenure} />
      )}
      {isTravelInsurance && hasPriceColumn && hasResolvedQuotes && (
        <QuoteBasisDisclaimer
          insuranceType={defaultInsuranceType}
          travelAsFamily={defaultTravelAsFamily}
          destination={defaultDestination?.label}
          region={defaultRegion?.label}
          endDateOffsetDays={endDateOffsetDays}
        />
      )}
      <div className="relative">
        <div
          className={classNames(
            'scrollbar-none flex flex-col',
            'overflow-hidden rounded-radius/table border border-borders/default',
            // Every breakpoint has a flexible track that fills the container,
            // so the frame takes the full width — except a table with no data
            // columns, where nothing flexes and the frame would otherwise put a
            // border around dead space.
            { 'w-fit max-w-full': !columnTemplates.hasFlexibleTrack },
          )}
        >
          <div
            ref={tableRef}
            // Horizontal scroll is now intended at every breakpoint, including
            // 768 (CMS-1772 R1 / CMS-1521), so both axes are declared rather
            // than leaning on the x-axis being computed from `overflow-y`.
            className={classNames('scrollbar-none overflow-auto')}
            onScroll={tableScrollHandler}
          >
            {/* The table is laid out as a grid, not by the table layout
                algorithm: only `minmax(min, 1fr)` tracks can share the leftover
                width equally while letting a single column freeze at its own
                minimum (CMS-1772 R2). `display` overrides drop the implicit
                table semantics, so every part carries an explicit ARIA role. */}
            <table
              role="table"
              className={classNames(
                // `min-w-min` is load-bearing for the sticky identity column.
                // The rows are `display: contents`, so each cell's containing
                // block is this grid container — and a sticky cell can only
                // travel to the edge of that box. At `w-full` alone the box is
                // just the viewport width while the tracks overflow well past
                // it, so the identity column unsticks after
                // `clientWidth - identityWidth` px and slides away (at 360:
                // after only 206 of 1510 scrollable px). `min-width: min-content`
                // grows the box to the track sum when the columns overflow and
                // is inert when they fit, so the flex share at 1280/1440 is
                // untouched.
                'grid w-full min-w-min',
                '[grid-template-columns:var(--tb-columns-sm)]',
                'md:[grid-template-columns:var(--tb-columns-md)]',
                'lg:[grid-template-columns:var(--tb-columns-lg)]',
                'xl:[grid-template-columns:var(--tb-columns-xl)]',
                'group',
                'relative',
                'scrollbar-none',
              )}
              style={responsiveTableTemplates}
            >
              <thead role="rowgroup" className="contents">
                <tr role="row" className="contents">
                  {/* Identity column header */}
                  <th
                    role="columnheader"
                    scope="col"
                    className={classNames(
                      // Neutral grey, so the reward column is the only coloured
                      // header and reads as the highlighted one (CMS-1772 R5).
                      classNames('bg-fill/alternative-2', NEUTRAL_HEADER_TEXT),
                      // `sticky` is for the horizontal axis only: there is
                      // no vertical scrollport here (row hiding handles length),
                      // so no `top-0`.
                      'z-40 sticky left-0',
                      // No minimum height here, unlike the body cells: the
                      // header row is sized by its labels, so a table whose
                      // captions all fit on one line gets a shorter header than
                      // one that wraps (CMS-1921 review item 7). The padding is
                      // the identity column's own, narrower on both axes so the
                      // fixed track spends less width on it (CMS-1772).
                      IDENTITY_CELL_PADDING,
                      'rounded-tl-radius/table',
                      {
                        'shadow-[4px_0px_6px_-1px_rgba(0,0,0,0.10)]': showLeft,
                      },
                    )}
                  >
                    <div className="flex h-full items-center">
                      <span className="font-font-weight/table-header text-font-size/table-header leading-line-height/table-header whitespace-break-spaces break-words text-left">
                        {/* The identity header is the one column caption the
                            front end owns in a PL table (CMS-1921 R3). */}
                        {isPersonalLoan ? staticText.loan : staticText.product}
                      </span>
                    </div>
                  </th>
                  {/* Data column headers */}
                  {renderedColumns.map(({ column: col }, position) => {
                    const isHotReward = col.type === 'HOT_REWARD_PICK';
                    return (
                      <th
                        key={col.id}
                        role="columnheader"
                        scope="col"
                        className={classNames(
                          // The Hot Reward Pick column keeps the brand tint; every
                          // other header is neutral grey (CMS-1772 R5).
                          isHotReward
                            ? classNames(
                                'bg-fill/primary-light',
                                HOT_REWARD_HEADER_TEXT,
                              )
                            : classNames(
                                'bg-fill/alternative-2',
                                NEUTRAL_HEADER_TEXT,
                              ),
                          // `relative`, not `sticky`: nothing to stick to on
                          // either axis, and being positioned is all `z-30`
                          // needs to lift the header over the body cells.
                          'z-30 relative',
                          // Content-sized, like the identity header above.
                          CELL_PADDING,
                          'border-l border-borders/default',
                          {
                            'rounded-tr-radius/table':
                              position === renderedColumns.length - 1,
                          },
                        )}
                      >
                        <div className="flex h-full items-center">
                          <span className="font-font-weight/table-header text-font-size/table-header leading-line-height/table-header whitespace-break-spaces break-words text-left">
                            {/* Locked label for Hot Reward Pick — the editor's
                                column.label is intentionally ignored (CMS-1687).
                                A TI_PREMIUM_PRICE column is an ordinary
                                attribute column: its header is the attribute
                                label / editor override, like any other.

                                On an attribute column the editor's label is an
                                override, so a blank one falls back to the
                                attribute's own label (CMS-1921, Kimchi Yim's B2
                                decision). That keeps the header following the
                                attribute per market instead of freezing the
                                wording it had when the column was set up. */}
                            {isHotReward
                              ? staticText.hot_reward_pick_title
                              : col.type === 'PL_TOTAL_REWARD'
                                ? // Locked too (CMS-1922 R2), but neutral grey.
                                  staticText.total_reward_title
                                : col.label || col.attributeKey?.label}
                          </span>
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody role="rowgroup" className="contents">
                {rows.map((row, index) => {
                  if (row.rowType === 'CATEGORY') {
                    return (
                      <CategoryRow
                        key={row.id}
                        categoryName={row.categoryName ?? ''}
                        totalColumns={totalColumns}
                      />
                    );
                  }

                  visibleRowCount++;
                  const isHidden =
                    !showAll &&
                    isShowMoreAvailable &&
                    visibleRowCount > MAX_VISIBLE_ROWS;

                  return (
                    <tr
                      key={row.id}
                      role="row"
                      // `contents` and `hidden` are both display values, so pick
                      // one rather than relying on Tailwind's rule order. The row
                      // paints no background of its own now that it has no box —
                      // hover lives on the cells via `group-hover/row`.
                      className={classNames(
                        'group/row',
                        isHidden ? 'hidden' : 'contents',
                      )}
                    >
                      <IdentityCell
                        row={row}
                        applyLabel={staticText.apply}
                        isScrolled={showLeft}
                        pushDataLayer={pushDataLayer}
                        index={index}
                        titleTableBuilder={title || 'Table builder'}
                        vertical={vertical}
                        defaultTripCriteria={defaultTripCriteria}
                        pageType={pageType}
                        loanSearchParams={loanSearchParams}
                      />
                      {renderedColumns.map(
                        ({ column: col, index: colIndex }) => {
                          const cell = resolveCell(col, row, colIndex, {
                            vertical,
                            pageType,
                            mode,
                            brand: context.brand,
                            // The context-fed engine facade (CMS-1896 R4);
                            // it carries the loader-or-dash answer with it.
                            resolveCalculated,
                            loanTerms,
                          });
                          return (
                            <ValueCell
                              key={cell.id}
                              cell={cell}
                              heightVariant={heightVariant}
                              showMoreLabel={staticText.show_more}
                              showLessLabel={staticText.show_less}
                              pushDataLayer={pushDataLayer}
                              index={index}
                              titleTableBuilder={title || 'Table builder'}
                            />
                          );
                        },
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {isShowMoreVisible && (
            <button
              type="button"
              className="col-span-full cursor-pointer overflow-hidden rounded-b-radius/table border-t border-borders/default p-2 bg-white hover:bg-fill/base md:p-3"
              onClick={() => setShowAll(true)}
            >
              <div className="flex flex-row items-center gap-2">
                <div className="mr-2 flex h-auto flex-none items-center gap-1 px-0 font-font-weight/bullet-2 text-buttons/neutral-dark alpha-bullet-2 text-font-size/bullet-2">
                  <ChevronRight className="size-6" />
                  <p className="mhg-alpha-typography-bullet-2">
                    {staticText.show_more} ({standardRows.length})
                  </p>
                </div>
              </div>
            </button>
          )}
        </div>
        <TableRuleButton
          type="left"
          onClick={() => {
            tableRef.current?.scrollTo({
              left: 0,
              top: 0,
              behavior: 'smooth',
            });
          }}
          show={showLeft}
        />
        <TableRuleButton
          type="right"
          onClick={() => {
            tableRef.current?.scrollTo({
              left: 10000,
              top: 0,
              behavior: 'smooth',
            });
          }}
          show={showRight}
        />
      </div>
    </div>
  );
};
